import type { VirtualPageViewPayload } from '~/types/tracking.types';

export enum TRACKING_GROUP {
  REGISTRATION_TRACKING = 'registration_tracking',
}

declare global {
  interface Window { dataLayer: any[] }
}

export type TRACKING_GROUP_TYPE =
  | TRACKING_GROUP.REGISTRATION_TRACKING;

export default function useTracking () {
  const gtmPush = (msg: any) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(msg);
  };

  const dispatchTrackingEvent = (
    eventName: string,
    eventProperties: Record<string, unknown> = {},
    trackingGroup?: TRACKING_GROUP_TYPE | undefined,
  ) => {
    if (trackingGroup) {
      const localStorageItem = localStorage.getItem(trackingGroup);
      const items = localStorageItem ? JSON.parse(localStorageItem) : {};

      if (!items[eventName]) {
        items[eventName] = eventProperties;
        localStorage.setItem(trackingGroup, JSON.stringify(items));
      } else {
        return;
      }
    }

    gtmPush({
      event: eventName,
      ...eventProperties,
    });
  };

  const virtualPageView = (contentGroup: string, secondaryContentGroup?: string) => {
    try {
      const { user } = useUserStore();

      const payload = {
        environment: document.location.hostname === 'app.medmastery.com' ? 'live' : 'test',
        content_area: 'app',
        content_group: contentGroup,
        secondary_content_group: secondaryContentGroup,
      } as VirtualPageViewPayload;

      if (user.value) {
        payload.user_id = user.value.traceId;
        payload.user_status = 'logged_in';
        payload.user_membership = user.value.subscription?.type || 'trial';
        payload.user_country = user.value.country;
        payload.user_profession = user.value.professionalTrack;
        payload.user_specialty = user.value.specialty;
        payload.user_experience = user.value.yearsOfExperience;
      } else {
        payload.user_status = 'logged_out';
      }

      gtmPush({
        'event': 'virtual_page_view',
        ... payload,
      });
    } catch(e) {
      console.error(e);
    }
  };

  const hashEmailAndPushOnBlur = async (event: FocusEvent) => {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();

    if (!value || value.length === 0) {
      return;
    }

    await hashEmailAndPush(value);
  };

  const hashEmailAndPush = async (email: string) => {
    try {
      const msgUint8 = new TextEncoder().encode(email);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      gtmPush({
        'event': 'ehcl_email',
        'sha256EmailAddress': hashHex,
      });

    } catch (error) {
      console.error(error);
    }
  };

  const clearGroupTracking = (trackingGroup: TRACKING_GROUP_TYPE) => {
    localStorage.removeItem(trackingGroup);
  };

  return {
    dispatchTrackingEvent,
    clearGroupTracking,
    hashEmailAndPush,
    hashEmailAndPushOnBlur,
    virtualPageView,
    gtmPush,
  };
}
